import { BoardRenderer } from '../renderer/boardRenderer';
import { Hud } from './hud';
import { Lobby } from './lobby';
import { openRulesModal } from './rulesModal';
import {
  GameState,
  GameMode,
  BotProfile,
  Card,
} from '../engine/types';
import {
  createGame,
  playCard,
  callBitIt,
  respondChallenge,
  surrenderRound,
  startRound,
} from '../engine/game';
import { decideBotAction } from '../engine/bot';
import {
  createRoom,
  joinRoom,
  updateRoomState,
  subscribeToRoom,
  fetchPublicRooms,
  generateRoomCode,
  RoomSession,
} from '../firebase/session';
import {
  t,
  getLocale,
  setLocale,
  onLocaleChange,
  Locale,
} from '../i18n';
import { isSoundEnabled, setSoundEnabled } from '../audio';

export class App {
  private rootElement: HTMLElement;
  private renderer: BoardRenderer | null = null;
  private hud: Hud | null = null;
  private lobby: Lobby | null = null;

  private gameState: GameState | null = null;
  private localPlayerId: string = 'player_human';
  private localPlayerName: string = 'Player 1';
  private isOnlineMode: boolean = false;
  private currentRoomSession: RoomSession | null = null;
  private roomUnsubscribe: (() => void) | null = null;

  private botTimer: number | null = null;
  private isHoldingTrickResolution: boolean = false;

  constructor(rootElement: HTMLElement) {
    this.rootElement = rootElement;
  }

  public async init(): Promise<void> {
    // Generate unique local player ID
    const savedId = localStorage.getItem('slimelings_player_id');
    if (savedId) {
      this.localPlayerId = savedId;
    } else {
      this.localPlayerId = `p_${Math.random().toString(36).substring(2, 8)}`;
      localStorage.setItem('slimelings_player_id', this.localPlayerId);
    }

    this.renderLayout();
    this.setupHeader();

    const canvasViewport = document.getElementById('canvas-viewport') as HTMLElement;
    const hudContainer = document.getElementById('hud-container') as HTMLElement;
    const lobbyContainer = document.getElementById('lobby-container') as HTMLElement;

    this.renderer = new BoardRenderer({
      onCardPlayed: (card) => this.handleLocalPlayerCardPlay(card),
    });
    await this.renderer.init(canvasViewport);
    this.renderer.setLocalPlayer(this.localPlayerId);

    this.hud = new Hud(hudContainer, {
      onCallBitIt: () => this.handleLocalPlayerCallBitIt(),
      onRespondChallenge: (resp) => this.handleLocalPlayerChallengeResponse(resp),
      onSurrender: () => this.handleLocalPlayerSurrender(),
      onPlayAgain: () => this.handlePlayAgain(),
      onBackToLobby: () => this.handleBackToLobby(),
      onSendEmote: (text) => this.handleSendEmote(text),
    });
    this.hud.setLocalPlayer(this.localPlayerId);

    this.lobby = new Lobby(lobbyContainer, {
      onStartSolo: (mode, botProfile) => this.startSoloGame(mode, botProfile),
      onCreateRoom: (mode, isPublic) => this.handleCreateRoom(mode, isPublic),
      onJoinRoom: (code) => this.handleJoinRoom(code),
      onStartOnlineGame: () => this.handleStartOnlineGame(),
      onAddBotToRoom: (profile) => this.handleAddBotToRoom(profile),
      onToggleRoomPublic: () => this.handleToggleRoomPublic(),
      onLeaveRoom: () => this.handleLeaveRoom(),
      onRefreshPublicRooms: () => this.refreshPublicRooms(),
    });
    this.lobby.setLocalPlayer(this.localPlayerId);

    onLocaleChange(() => {
      this.setupHeader();
      if (this.gameState) {
        this.hud?.update(this.gameState);
      }
      this.lobby?.render();
    });

    // Check URL parameters for direct room invite: ?room=SLIME-8
    const params = new URLSearchParams(window.location.search);
    const roomParam = params.get('room');
    if (roomParam) {
      this.handleJoinRoom(roomParam);
    } else {
      this.showLobby();
    }
  }

  private renderLayout(): void {
    this.rootElement.innerHTML = `
      <header class="top-header" id="top-header"></header>
      <div id="game-stage">
        <div id="canvas-viewport"></div>
        <div id="hud-container"></div>
      </div>
      <div id="lobby-container"></div>
    `;
  }

  private setupHeader(): void {
    const header = document.getElementById('top-header');
    if (!header) return;

    document.title = `${t('app_title')} — ${t('app_subtitle')}`;

    const currentLoc = getLocale();
    const soundActive = isSoundEnabled();

    // Clean inline SVG flags
    const flagSvg: Record<Locale, string> = {
      'pt-BR': `<svg viewBox="0 0 720 504" width="20" height="14" class="flag-icon"><rect width="720" height="504" fill="#009c3b"/><polygon points="360,40 680,252 360,464 40,252" fill="#ffdf00"/><circle cx="360" cy="252" r="126" fill="#002776"/><path d="M 234,252 A 136,136 0 0,0 486,252 A 126,126 0 0,1 234,252" fill="#ffffff"/></svg>`,
      'en': `<svg viewBox="0 0 741 390" width="20" height="14" class="flag-icon"><rect width="741" height="390" fill="#b22234"/><path d="M0,30H741M0,90H741M0,150H741M0,210H741M0,270H741M0,330H741" stroke="#fff" stroke-width="30"/><rect width="296" height="210" fill="#3c3b6e"/></svg>`,
      'pl': `<svg viewBox="0 0 640 400" width="20" height="14" class="flag-icon" style="border: 1px solid rgba(0,0,0,0.15);"><rect width="640" height="200" fill="#ffffff"/><rect y="200" width="640" height="200" fill="#dc143c"/></svg>`,
    };

    let navButtonHtml = '';
    if (this.isOnlineMode && this.currentRoomSession) {
      navButtonHtml = `
        <button class="btn-icon" id="btn-leave-header" title="${t('btn_leave')}">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
          <span class="rules-label">${t('btn_leave')}</span>
        </button>
      `;
    } else if (this.gameState) {
      navButtonHtml = `
        <button class="btn-icon" id="btn-leave-header" title="${t('btn_main_menu')}">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
          <span class="rules-label">${t('btn_main_menu')}</span>
        </button>
      `;
    }

    const soundSvg = soundActive
      ? `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>`
      : `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>`;

    header.innerHTML = `
      <div class="brand-section">
        <img src="./assets/Logo.png" alt="Slimelings Bit-It!" class="brand-logo" />
        <span class="brand-slogan">"${t('app_slogan')}"</span>
      </div>
      <div class="header-controls">
        ${navButtonHtml}
        <button class="btn-icon" id="btn-rules" title="${t('tab_rules')}">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
          <span class="rules-label">${t('tab_rules')}</span>
        </button>
        <button class="btn-icon" id="btn-sound" title="${soundActive ? t('sound_on') : t('sound_off')}">
          ${soundSvg}
        </button>
        <div class="lang-switch-group">
          <button class="lang-btn ${currentLoc === 'pt-BR' ? 'active' : ''}" data-lang="pt-BR" title="Português (BR)">
            ${flagSvg['pt-BR']} <span>PT</span>
          </button>
          <button class="lang-btn ${currentLoc === 'en' ? 'active' : ''}" data-lang="en" title="English (US)">
            ${flagSvg['en']} <span>EN</span>
          </button>
          <button class="lang-btn ${currentLoc === 'pl' ? 'active' : ''}" data-lang="pl" title="Polski (PL)">
            ${flagSvg['pl']} <span>PL</span>
          </button>
        </div>
      </div>
    `;

    header.querySelector('#btn-leave-header')?.addEventListener('click', () => {
      if (this.currentRoomSession) {
        this.handleLeaveRoom();
      } else if (this.gameState) {
        this.handleBackToLobby();
      } else {
        this.showLobby();
      }
    });

    header.querySelector('#btn-rules')?.addEventListener('click', () => {
      openRulesModal();
    });

    header.querySelector('#btn-sound')?.addEventListener('click', () => {
      const nextSound = !isSoundEnabled();
      setSoundEnabled(nextSound);
      this.setupHeader();
    });

    header.querySelectorAll('.lang-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = (e.currentTarget as HTMLElement).getAttribute('data-lang') as Locale;
        if (target) {
          setLocale(target);
        }
      });
    });
  }

  public showLobby(): void {
    const lobbyContainer = document.getElementById('lobby-container');
    const hudContainer = document.getElementById('hud-container');
    if (lobbyContainer) lobbyContainer.style.display = 'block';
    if (hudContainer) hudContainer.style.display = 'none';
    this.setupHeader();
    this.lobby?.render();
  }

  public hideLobby(): void {
    const lobbyContainer = document.getElementById('lobby-container');
    const hudContainer = document.getElementById('hud-container');
    if (lobbyContainer) lobbyContainer.style.display = 'none';
    if (hudContainer) hudContainer.style.display = 'block';
    this.setupHeader();
  }

  // --- Solo Game Flow ---
  public startSoloGame(mode: GameMode, botProfile: BotProfile): void {
    this.isOnlineMode = false;
    this.currentRoomSession = null;
    if (this.roomUnsubscribe) {
      this.roomUnsubscribe();
      this.roomUnsubscribe = null;
    }

    let playersConfig: Array<{ id: string; name: string; isBot: boolean; botProfile?: BotProfile; team?: 'A' | 'B' }>;

    if (mode === 'duel') {
      const levelLabel = t(botProfile === 'aggressive' ? 'bot_level_3' : botProfile === 'cautious' ? 'bot_level_1' : 'bot_level_2');
      playersConfig = [
        { id: this.localPlayerId, name: this.localPlayerName, isBot: false },
        { id: 'bot_1', name: `Bot (${levelLabel})`, isBot: true, botProfile },
      ];
    } else if (mode === 'teams') {
      const oppLevelLabel = t(botProfile === 'aggressive' ? 'bot_level_3' : botProfile === 'cautious' ? 'bot_level_1' : 'bot_level_2');
      playersConfig = [
        { id: this.localPlayerId, name: this.localPlayerName, isBot: false, team: 'A' },
        { id: 'bot_opp_1', name: `Bot 1 (${oppLevelLabel})`, isBot: true, botProfile, team: 'B' },
        { id: 'bot_partner', name: `Parceiro (${t('bot_level_2')})`, isBot: true, botProfile: 'minimax', team: 'A' },
        { id: 'bot_opp_2', name: `Bot 2 (${oppLevelLabel})`, isBot: true, botProfile, team: 'B' },
      ];
    } else {
      // 4P FFA
      playersConfig = [
        { id: this.localPlayerId, name: this.localPlayerName, isBot: false },
        { id: 'bot_1', name: `Bot (${t('bot_level_1')})`, isBot: true, botProfile: 'cautious' },
        { id: 'bot_2', name: `Bot (${t('bot_level_2')})`, isBot: true, botProfile: 'minimax' },
        { id: 'bot_3', name: `Bot (${t('bot_level_3')})`, isBot: true, botProfile: 'aggressive' },
      ];
    }

    this.gameState = createGame(mode, playersConfig);
    this.hideLobby();
    this.updateGameState(this.gameState);
  }

  private updateGameState(state: GameState): void {
    const prevCompletedCount = this.gameState ? this.gameState.completedTricks.length : 0;
    this.gameState = state;

    // Check if trick just completed to add a smooth 2.5s delay highlighting winner
    const trickJustCompleted = state.completedTricks.length > prevCompletedCount;

    if (trickJustCompleted) {
      const lastCompletedTrick = state.completedTricks[state.completedTricks.length - 1];
      this.isHoldingTrickResolution = true;
      this.renderer?.showTrickResolution(lastCompletedTrick);
      this.hud?.update(state);

      if (this.botTimer) clearTimeout(this.botTimer);

      setTimeout(() => {
        this.isHoldingTrickResolution = false;
        this.renderer?.clearTrickResolution();
        if (this.gameState) {
          this.renderer?.updateState(this.gameState);
          this.hud?.update(this.gameState);

          if (this.isOnlineMode && this.currentRoomSession && this.currentRoomSession.hostId === this.localPlayerId) {
            updateRoomState(this.currentRoomSession.id, { gameState: this.gameState });
          }

          if (this.gameState.status === 'playing') {
            this.scheduleBotActionIfNeeded();
          } else if (this.gameState.status === 'round_end' && !this.gameState.matchWinnerId) {
            this.hud?.showRoundEndModal(this.gameState, () => this.handleNextRound(), this.isOnlineMode);
          } else if (this.gameState.status === 'match_end' && this.gameState.matchWinnerId) {
            this.hud?.showMatchEndModal(this.gameState);
          }
        }
      }, 2500);
      return;
    }

    this.renderer?.updateState(state);
    this.hud?.update(state);

    if (this.isOnlineMode && this.currentRoomSession && this.currentRoomSession.hostId === this.localPlayerId) {
      updateRoomState(this.currentRoomSession.id, { gameState: state });
    }

    // Check for bot actions
    this.scheduleBotActionIfNeeded();

    // Show round/match end modal if round ended without trick delay (e.g. fold)
    if (state.status === 'round_end' && !state.matchWinnerId) {
      this.hud?.showRoundEndModal(state, () => this.handleNextRound(), this.isOnlineMode);
    } else if (state.status === 'match_end' && state.matchWinnerId) {
      setTimeout(() => {
        if (this.gameState?.status === 'match_end') {
          this.hud?.showMatchEndModal(this.gameState);
        }
      }, 1000);
    }
  }

  private handleNextRound(): void {
    if (!this.gameState || this.gameState.status !== 'round_end') return;
    const nextState = startRound(this.gameState);
    this.updateGameState(nextState);
  }

  private scheduleBotActionIfNeeded(): void {
    if (!this.gameState || this.gameState.status !== 'playing') return;
    if (this.isHoldingTrickResolution) return;
    if (this.botTimer) clearTimeout(this.botTimer);

    // Is there a bot required to respond to a pending challenge?
    if (this.gameState.challenge.status === 'pending') {
      const pendingBotId = Object.keys(this.gameState.challenge.responses).find((pid) => {
        const player = this.gameState!.players.find((p) => p.id === pid);
        return player?.isBot && this.gameState!.challenge.responses[pid] === 'pending';
      });

      if (pendingBotId) {
        this.botTimer = window.setTimeout(() => {
          this.executeBotTurn(pendingBotId);
        }, 1300);
        return;
      }
    }

    // Is it a bot's turn to play or call Bit-It?
    const currentTurnPlayer = this.gameState.players.find(
      (p) => p.id === this.gameState!.currentTurnPlayerId
    );
    if (currentTurnPlayer?.isBot && this.gameState.challenge.status !== 'pending') {
      this.botTimer = window.setTimeout(() => {
        this.executeBotTurn(currentTurnPlayer.id);
      }, 1200);
    }
  }

  private executeBotTurn(botId: string): void {
    if (!this.gameState || this.gameState.status !== 'playing') return;
    if (this.isHoldingTrickResolution) return;

    try {
      const decision = decideBotAction(this.gameState, botId);

      if (decision.action === 'respond_challenge' && decision.challengeResponse) {
        const nextState = respondChallenge(this.gameState, botId, decision.challengeResponse);
        this.updateGameState(nextState);
      } else if (decision.action === 'call_bit_it') {
        const nextState = callBitIt(this.gameState, botId);
        this.updateGameState(nextState);
      } else if (decision.action === 'play_card' && decision.cardId) {
        const nextState = playCard(this.gameState, botId, decision.cardId);
        this.updateGameState(nextState);
      }
    } catch (err) {
      console.warn(`Bot ${botId} decision error:`, err);
      // Failsafe recovery so game flow never hangs
      if (this.gameState.challenge.status === 'pending' && this.gameState.challenge.responses[botId] === 'pending') {
        const fallback = respondChallenge(this.gameState, botId, 'face');
        this.updateGameState(fallback);
      } else if (this.gameState.currentTurnPlayerId === botId) {
        const bot = this.gameState.players.find((p) => p.id === botId);
        if (bot && bot.hand.length > 0) {
          const fallback = playCard(this.gameState, botId, bot.hand[0].id);
          this.updateGameState(fallback);
        }
      }
    }
  }

  // --- Local Human Player Actions ---
  private handleLocalPlayerCardPlay(card: Card): void {
    if (!this.gameState || this.gameState.status !== 'playing') {
      if (this.gameState) this.renderer?.updateState(this.gameState);
      return;
    }
    if (this.isHoldingTrickResolution) {
      this.renderer?.updateState(this.gameState);
      return;
    }
    if (this.gameState.currentTurnPlayerId !== this.localPlayerId) {
      this.renderer?.updateState(this.gameState);
      return;
    }

    try {
      const nextState = playCard(this.gameState, this.localPlayerId, card.id);
      this.updateGameState(nextState);
    } catch (err) {
      console.warn('Cannot play card:', err);
      this.renderer?.updateState(this.gameState);
    }
  }

  private handleLocalPlayerCallBitIt(): void {
    if (!this.gameState) return;
    try {
      const nextState = callBitIt(this.gameState, this.localPlayerId);
      this.updateGameState(nextState);
    } catch (err) {
      console.warn('Cannot call Bit-It:', err);
    }
  }

  private handleLocalPlayerChallengeResponse(response: 'face' | 'fold'): void {
    if (!this.gameState) return;
    try {
      const nextState = respondChallenge(this.gameState, this.localPlayerId, response);
      this.updateGameState(nextState);
    } catch (err) {
      console.warn('Cannot respond challenge:', err);
    }
  }

  private handleLocalPlayerSurrender(): void {
    if (!this.gameState || this.gameState.status !== 'playing') return;
    try {
      const nextState = surrenderRound(this.gameState, this.localPlayerId);
      this.updateGameState(nextState);
    } catch (err) {
      console.warn('Cannot surrender:', err);
    }
  }

  private handlePlayAgain(): void {
    if (this.isOnlineMode && this.currentRoomSession) {
      if (this.currentRoomSession.hostId === this.localPlayerId) {
        this.handleStartOnlineGame();
      }
    } else if (this.gameState) {
      this.startSoloGame(this.gameState.mode, 'minimax');
    }
  }

  private handleBackToLobby(): void {
    if (this.roomUnsubscribe) {
      this.roomUnsubscribe();
      this.roomUnsubscribe = null;
    }
    this.currentRoomSession = null;
    this.isOnlineMode = false;
    this.gameState = null;
    this.lobby?.setSession(null);
    this.showLobby();
  }

  private handleSendEmote(text: string): void {
    if (!this.gameState) return;
    const player = this.gameState.players.find((p) => p.id === this.localPlayerId);
    const updatedState: GameState = {
      ...this.gameState,
      logs: [
        ...this.gameState.logs,
        {
          id: `log_emote_${Date.now()}`,
          timestamp: Date.now(),
          messageKey: 'event_player_emote',
          params: { name: player?.name || this.localPlayerName, emote: text },
        },
      ],
    };
    this.updateGameState(updatedState);

    // Also broadcast if online
    if (this.isOnlineMode && this.currentRoomSession) {
      const newChat = [
        ...this.currentRoomSession.chat,
        { id: `chat_${Date.now()}`, sender: player?.name || this.localPlayerName, text, timestamp: Date.now() },
      ];
      updateRoomState(this.currentRoomSession.id, { chat: newChat });
    }
  }

  // --- Multiplayer Flow ---
  private async handleCreateRoom(mode: GameMode, isPublic: boolean): Promise<void> {
    try {
      const code = generateRoomCode();
      const session = await createRoom(code, this.localPlayerId, this.localPlayerName, mode, isPublic);
      this.isOnlineMode = true;
      this.currentRoomSession = session;
      this.subscribeSession(session.id);
      this.lobby?.setSession(session);
    } catch (err) {
      console.warn('Failed to create room in Firebase Firestore, running local simulated room:', err);
      // Fallback: local simulated multiplayer room
      const code = generateRoomCode();
      this.currentRoomSession = {
        id: code,
        hostId: this.localPlayerId,
        mode,
        isPublic,
        status: 'waiting',
        players: [{ id: this.localPlayerId, name: this.localPlayerName, isBot: false, score: 0, connected: true }],
        chat: [],
        updatedAt: Date.now(),
      };
      this.isOnlineMode = true;
      this.lobby?.setSession(this.currentRoomSession);
    }
  }

  private async handleJoinRoom(code: string): Promise<void> {
    try {
      const session = await joinRoom(code, this.localPlayerId, this.localPlayerName);
      this.isOnlineMode = true;
      this.currentRoomSession = session;
      this.subscribeSession(session.id);
      this.lobby?.setSession(session);
    } catch (err) {
      console.warn('Join room error:', err);
      alert(t('error_room_not_found'));
    }
  }

  private subscribeSession(roomId: string): void {
    if (this.roomUnsubscribe) this.roomUnsubscribe();
    this.roomUnsubscribe = subscribeToRoom(roomId, (session) => {
      this.currentRoomSession = session;
      this.lobby?.setSession(session);

      if (session.status === 'playing' && session.gameState) {
        this.gameState = session.gameState;
        this.hideLobby();
        this.updateGameState(session.gameState);
      }
    });
  }

  private async handleStartOnlineGame(): Promise<void> {
    if (!this.currentRoomSession) return;
    const playersConfig = this.currentRoomSession.players.map((p) => ({
      id: p.id,
      name: p.name,
      isBot: p.isBot,
      botProfile: p.botProfile || 'minimax',
      team: p.team,
    }));

    const initialState = createGame(this.currentRoomSession.mode, playersConfig);
    this.gameState = initialState;
    this.hideLobby();
    this.updateGameState(initialState);

    await updateRoomState(this.currentRoomSession.id, {
      status: 'playing',
      gameState: initialState,
    });
  }

  private async handleAddBotToRoom(profile: BotProfile): Promise<void> {
    if (!this.currentRoomSession) return;
    const max = this.currentRoomSession.mode === 'duel' ? 2 : 4;
    if (this.currentRoomSession.players.length >= max) return;

    const botNumber = this.currentRoomSession.players.length;
    const botPlayer = {
      id: `bot_${botNumber}`,
      name: `Bot ${botNumber} (${t(profile === 'aggressive' ? 'bot_level_3' : profile === 'cautious' ? 'bot_level_1' : 'bot_level_2')})`,
      isBot: true,
      botProfile: profile,
      score: 0,
      connected: true,
    };

    const updatedPlayers = [...this.currentRoomSession.players, botPlayer];
    this.currentRoomSession.players = updatedPlayers;
    this.lobby?.setSession(this.currentRoomSession);

    try {
      await updateRoomState(this.currentRoomSession.id, { players: updatedPlayers });
    } catch {
      // offline mode
    }
  }

  private async handleToggleRoomPublic(): Promise<void> {
    if (!this.currentRoomSession) return;
    const nextPublic = !this.currentRoomSession.isPublic;
    this.currentRoomSession.isPublic = nextPublic;
    this.lobby?.setSession(this.currentRoomSession);

    try {
      await updateRoomState(this.currentRoomSession.id, { isPublic: nextPublic });
    } catch {
      // offline mode
    }
  }

  private async handleLeaveRoom(): Promise<void> {
    if (this.roomUnsubscribe) {
      this.roomUnsubscribe();
      this.roomUnsubscribe = null;
    }
    const session = this.currentRoomSession;
    const isOnline = this.isOnlineMode;

    // Reset local state immediately so user is never blocked
    this.currentRoomSession = null;
    this.isOnlineMode = false;
    this.gameState = null;
    this.lobby?.setSession(null);
    this.showLobby();

    if (session && isOnline) {
      try {
        const remainingPlayers = session.players.filter((p) => p.id !== this.localPlayerId);
        if (remainingPlayers.length === 0 || session.hostId === this.localPlayerId) {
          updateRoomState(session.id, { status: 'finished' });
        } else {
          updateRoomState(session.id, { players: remainingPlayers });
        }
      } catch (err) {
        console.warn('Error on room exit:', err);
      }
    }
  }

  private async refreshPublicRooms(): Promise<void> {
    const rooms = await fetchPublicRooms();
    this.lobby?.setPublicRooms(rooms);
  }
}
